/**
 * Lightweight i18n helpers for Canada EN/FR localization.
 * No third-party library — only two locales and fixed route prefixes.
 */

export type Locale = "en" | "fr";
export type Country = "US" | "CA";

export type LocaleContext = {
  locale: Locale;
  country: Country;
};

/** Paths that receive geo redirects onto /ca or /ca/fr. */
export const CORE_GEO_PATHS = new Set([
  "/",
  "/web-support",
  "/hosting",
  "/pricing",
  "/help",
  "/help/faqs",
  "/contact",
]);

export const GEO_COOKIE = "cloudaxe-geo";
export type GeoCookieValue = "us" | "ca" | "ca-fr";

export function isLocale(value: string): value is Locale {
  return value === "en" || value === "fr";
}

/**
 * Derive locale + country from a pathname.
 * /ca/fr/... -> fr + CA; /ca/... -> en + CA; else en + US.
 */
export function deriveLocaleFromPath(pathname: string): LocaleContext {
  if (pathname === "/ca/fr" || pathname.startsWith("/ca/fr/")) {
    return { locale: "fr", country: "CA" };
  }
  if (pathname === "/ca" || pathname.startsWith("/ca/")) {
    return { locale: "en", country: "CA" };
  }
  return { locale: "en", country: "US" };
}

/** Strip /ca or /ca/fr prefix, returning the canonical US path. */
export function stripLocalePrefix(pathname: string): string {
  if (pathname === "/ca/fr" || pathname === "/ca") return "/";
  if (pathname.startsWith("/ca/fr/")) {
    const rest = pathname.slice("/ca/fr".length);
    return rest || "/";
  }
  if (pathname.startsWith("/ca/")) {
    const rest = pathname.slice("/ca".length);
    return rest || "/";
  }
  return pathname;
}

/**
 * Prefix an internal path for the active locale/country.
 * External URLs and hashes-only are returned unchanged.
 */
export function localizeHref(
  path: string,
  ctx: LocaleContext | Locale
): string {
  const { locale, country } =
    typeof ctx === "string" ? deriveContextFromLocale(ctx) : ctx;

  if (
    !path ||
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("mailto:") ||
    path.startsWith("tel:") ||
    path.startsWith("#")
  ) {
    return path;
  }

  // Keep privacy/terms/apps on global (non-localized) routes for now.
  const bare = stripLocalePrefix(path.split("?")[0] ?? path);
  const queryIndex = path.indexOf("?");
  const query = queryIndex >= 0 ? path.slice(queryIndex) : "";
  const hashIndex = path.indexOf("#");
  // If path is "/#services" style — stripLocalePrefix on full path is fine for bare.
  const hashOnly = path.startsWith("/#") || (path.startsWith("#") && !path.startsWith("#/"));

  if (country !== "CA") {
    return path;
  }

  // Hash-only anchors on home: localize the home base.
  if (path.startsWith("/#")) {
    const prefix = locale === "fr" ? "/ca/fr" : "/ca";
    return `${prefix}/${path.slice(1)}${query}`; // path.slice(1) keeps "#..."
  }
  if (hashOnly) return path;

  if (!CORE_GEO_PATHS.has(bare) && bare !== "/") {
    // Non-core paths (apps, privacy, terms) stay unprefixed.
    return path;
  }

  const prefix = locale === "fr" ? "/ca/fr" : "/ca";
  if (bare === "/") {
    return `${prefix}${query}`;
  }
  return `${prefix}${bare}${query}`;
}

function deriveContextFromLocale(locale: Locale): LocaleContext {
  // Locale alone implies Canada when calling with "fr"; "en" without country
  // defaults to US. Prefer passing LocaleContext explicitly.
  return locale === "fr"
    ? { locale: "fr", country: "CA" }
    : { locale: "en", country: "US" };
}

/** Switch between /ca and /ca/fr while preserving the rest of the path. */
export function switchLocalePath(pathname: string, targetLocale: Locale): string {
  const bare = stripLocalePrefix(pathname);
  const ctx: LocaleContext = {
    locale: targetLocale,
    country: "CA",
  };
  return localizeHref(bare, ctx);
}

export function prefersFrench(acceptLanguage: string | null): boolean {
  if (!acceptLanguage) return false;
  const primary = acceptLanguage.split(",")[0]?.trim().toLowerCase() ?? "";
  return primary.startsWith("fr");
}

export function geoTargetFromLanguage(acceptLanguage: string | null): {
  prefix: "/ca" | "/ca/fr";
  cookie: GeoCookieValue;
} {
  if (prefersFrench(acceptLanguage)) {
    return { prefix: "/ca/fr", cookie: "ca-fr" };
  }
  return { prefix: "/ca", cookie: "ca" };
}
